import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PATHS } from 'models/bary-paths.model';
import { IBaryModalEvent } from '../../../../models/bary-modal-event';
import { mapAsBody } from '../../util/request-util';
import { Transporter } from '../../../../models/transporter.model';
import { OfferService } from '../../../../services/offer.service';
import { InsuranceItemService } from '../../../../services/insurance-item.service';
import { TranslateService } from '@ngx-translate/core';
import { TransportOrderExtendedService } from '../../../../services/transport-order-extended.service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { CurrentDataService } from '../../../../services/current-data-service';
import { TransportOrderView } from '../../../../models/transport-order-view.model';
import { PaymentMethodEnum } from '../../../../models/enums/payment-method.enum';
import { TransportOrder } from '../../../../models/transport-order.model';
import { Transport } from '../../../../models/transport.model';
import { DeliveryStatusEnum } from '../../../../models/enums/delivery-status.enum';
import { ErrorsCodeEnum } from '../../../../models/enums/errors-code.enum';
import { AlertService } from '../../../../services/alert.service';
import { DeliveryStatus } from '../../../../models/delivery-status.model';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';

@Component({
  selector: 'jhi-transport-actions',
  templateUrl: './transport-actions.component.html',
})
export class TransportActionsComponent implements OnInit {
  readonly #translateService = inject(TranslateService);
  readonly #alertService = inject(AlertService);
  readonly #transportOrderService = inject(TransportOrderExtendedService);
  readonly #modalService = inject(BaryModalService);
  readonly #router = inject(Router);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #offerService = inject(OfferService);
  readonly #insuranceItemService = inject(InsuranceItemService);

  @Input() public transportOrder!: TransportOrder;
  @Input() public ediId?: string;

  public tooltip = '';

  public isQuotation = false;
  public isDeletable = false;

  public transport?: Transport;
  public transporter?: Transporter;

  protected readonly FAS = FaDirectiveEnum.FAS;

  public ngOnInit(): void {
    this.#transportOrderService.checkTransportIsDeletable(this.transportOrder as TransportOrderView).subscribe(value => {
      this.isDeletable = !!value;
    });
    this.#currentDataService.getCurrentTransporter().subscribe(value => {
      if (value) {
        this.transporter = value;
      }
    });
    this.getToolTipMessage();
    this.createTransportFromTransportOrder();
  }

  public sendEdi(): void {
    this.#modalService
      .openConfirm(this.#translateService.instant('TRANSPORT_ACTIONS.RESEND', { id: this.transportOrder.id }))
      .action()
      .subscribe((next: IBaryModalEvent) => {
        if (next.submit) {
          this.#transportOrderService.sendEdi(this.transportOrder as TransportOrderView).subscribe(() => {
            this.#modalService
              .openInfo(this.#translateService.instant('TRANSPORT_ACTIONS.RESEND_CONFIRM', { id: this.transportOrder.id }))
              .action()
              .subscribe();
          });
        }
      });
  }

  public deleteTransport(): void {
    this.#modalService
      .openConfirm(this.#translateService.instant('MODAL_CONFIRM.DELETE_TRANSPORT_ORDER', { id: this.transportOrder.id }))
      .action()
      .subscribe((next: IBaryModalEvent) => {
        if (next.submit && this.transportOrder.id) {
          this.#transportOrderService.delete(this.transportOrder.id).subscribe({
            next: (): void => {
              this.#modalService
                .openInfo(this.#translateService.instant('TRANSPORT_ACTIONS.DELETE_CONFIRM', { id: this.transportOrder.id }))
                .action()
                .subscribe(() => {
                  this.#router.navigate([PATHS.MIDDLE_TRANSPORTS.path]).then();
                });
            },
            error: (error): void => {
              let message;
              if (error.error.baryError.errorCode === ErrorsCodeEnum.ERROR_CANCEL_TRANSPORT_ORDER_CB) {
                message = this.#translateService.instant('ERROR.CANCEL_TRANSPORT_ORDER.CB');
              } else if (error.error.baryError.errorCode === ErrorsCodeEnum.ERROR_CANCEL_TRANSPORT_ORDER_BILL) {
                message = this.#translateService.instant('ERROR.CANCEL_TRANSPORT_ORDER.BILL');
              } else if (error.error.baryError.errorCode === ErrorsCodeEnum.ERROR_CANCEL_TRANSPORT_ORDER_EDI) {
                message = this.#translateService.instant('ERROR.CANCEL_TRANSPORT_ORDER.EDI');
              } else {
                message = this.#translateService.instant('ERROR.GENERIC');
              }
              this.#alertService.addAlert({ message, type: 'danger' });
            },
          });
        }
      });
  }

  public goToUpdateTransport(): void {
    this.#modalService
      .openConfirm(this.#translateService.instant('TRANSPORT_ACTIONS.REDIRECT_TO_UPDATE_PAGE', { id: this.transportOrder.id }))
      .action()
      .subscribe((next: IBaryModalEvent) => {
        if (next.submit && this.transportOrder.id) {
          this.#router.navigateByUrl(PATHS.MIDDLE_TRANSPORT_ID_UPDATE.withId(this.transportOrder.id)).then();
        }
      });
  }

  public isDisabledButtonModify(): boolean {
    return this.transportOrder.paymentMethod === PaymentMethodEnum.CB || this.transportOrder.basketOrder != null;
  }

  public confirmDelivery(): void {
    this.#modalService
      .openConfirmDelivery(this.transportOrder.id!)
      .action()
      .subscribe(action => {
        if (action.submit) {
          window.history.back();
        }
      });
  }

  private getToolTipMessage(): void {
    this.#offerService.find(this.transportOrder.offerId!).subscribe(offer => {
      this.isQuotation = offer.isQuotation;
      if (this.isDisabledButtonModify() && !this.isQuotation) {
        this.tooltip = this.#translateService.instant('TRANSPORT_ACTIONS.BUTTON_MODIFY.TOOLTIP', { id: this.transportOrder.id });
      } else if (this.isQuotation) {
        this.tooltip = this.#translateService.instant('TRANSPORT_ACTIONS.BUTTON_MODIFY.TOOLTIP_QUOTATION', { id: this.transportOrder.id });
      }
    });
  }

  private createTransportFromTransportOrder(): void {
    this.transport = new Transport(this.transportOrder.serialize());
    this.transport.id = undefined;

    if (this.transportOrder.offerId) {
      this.transport!.offerId = this.transportOrder.offerId;
      this.#insuranceItemService
        .findAllByOfferId(this.transport!.offerId!)
        .pipe(mapAsBody)
        .subscribe(insurances => {
          const insurance = (insurances || []).find(it => it.itemsNature === this.transportOrder.insurance + '');
          if (insurance) {
            this.transport!.insuranceItem = insurance;
          }
          this.#currentDataService.updateCurrentTransport(this.transport!);
        });
    }
  }
  // DELIVERY_STATUS_CANCELED
  public isDeliveryStatusCanceled(status: DeliveryStatusEnum | DeliveryStatus): boolean {
    return (status instanceof DeliveryStatus && status.code === DeliveryStatusEnum.CANCELED) || status === DeliveryStatusEnum.CANCELED;
  }
}
